import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { focusContainerOnTouch, IDENTIFIER_FIELD_PROPS, isCoarsePointer, NAME_FIELD_PROPS, } from '@/components/shared/touchInput';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { EyeOff } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
/** DICOM VR limits, mirrored from the server's StudyAnonymizeRequest. */
const PERSON_NAME_MAX = 64;
const LONG_STRING_MAX = 64;
const SHORT_STRING_MAX = 16;
/** A fresh placeholder ID so two copies of one study are never confused. */
export function placeholderPatientId() {
    return `ANON-${crypto.randomUUID().slice(0, 8).toUpperCase()}`;
}
/** The source's calendar day in the browser's zone, for `<input type="date">`. */
export function sourceDateValue(iso, today = new Date()) {
    const d = iso ? new Date(iso) : today;
    const date = Number.isNaN(d.getTime()) ? today : d;
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
export function AnonymizeStudyDialog(props) {
    return (_jsx(Dialog, { open: props.open, onOpenChange: props.onOpenChange, children: _jsx(DialogContent, { onOpenAutoFocus: focusContainerOnTouch, className: "max-h-[90dvh] overflow-y-auto sm:max-w-lg", "data-testid": "anonymize-study-dialog", children: props.open && _jsx(AnonymizeStudyDialogBody, { ...props }) }) }));
}
function AnonymizeStudyDialogBody({ study, onOpenChange, onSubmit, isLoading = false, }) {
    const { t } = useTranslation();
    const [patientName, setPatientName] = useState('Anonymous');
    const [patientId, setPatientId] = useState(() => placeholderPatientId());
    const [ownerName, setOwnerName] = useState('Anonymous');
    const [accessionNumber, setAccessionNumber] = useState('');
    const [institutionName, setInstitutionName] = useState('');
    const [referringPhysician, setReferringPhysician] = useState('');
    const [description, setDescription] = useState(study.study.description ?? '');
    const [studyDate, setStudyDate] = useState(() => sourceDateValue(study.study.dateTime));
    const [keepBirthDate, setKeepBirthDate] = useState(true);
    const [keepSex, setKeepSex] = useState(true);
    const [keepWeight, setKeepWeight] = useState(true);
    const [keepSpecies, setKeepSpecies] = useState(true);
    const [keepBreed, setKeepBreed] = useState(true);
    // Off by default: whatever a tech typed at the modality may name the patient or owner.
    const [keepSeriesDescriptions, setKeepSeriesDescriptions] = useState(false);
    const todayValue = sourceDateValue(null);
    const problem = (() => {
        if (!patientName.trim() || !patientId.trim() || !ownerName.trim())
            return 'anonymize.error.required';
        if ([patientName, ownerName, referringPhysician].some((v) => v.trim().length > PERSON_NAME_MAX) ||
            [patientId, institutionName, description].some((v) => v.trim().length > LONG_STRING_MAX) ||
            accessionNumber.trim().length > SHORT_STRING_MAX)
            return 'anonymize.error.tooLong';
        if (!studyDate)
            return 'anonymize.error.date';
        if (studyDate > todayValue)
            return 'anonymize.error.futureDate';
        return null;
    })();
    const notRecorded = t('anonymize.notRecorded');
    const current = (value) => value?.trim() || notRecorded;
    const sexSummary = [
        study.patient.sex,
        study.patient.sexNeutered === true ? t('anonymize.neutered') : null,
    ]
        .filter(Boolean)
        .join(', ');
    const handleSubmit = (e) => {
        e.preventDefault();
        if (problem)
            return;
        const blank = (v) => (v.trim() ? v.trim() : null);
        onSubmit({
            patientName: patientName.trim(),
            patientId: patientId.trim(),
            ownerName: ownerName.trim(),
            accessionNumber: blank(accessionNumber),
            institutionName: blank(institutionName),
            referringPhysician: blank(referringPhysician),
            description: blank(description),
            studyDate,
            keepBirthDate,
            keepSex,
            keepWeight,
            keepSpecies,
            keepBreed,
            keepSeriesDescriptions,
        });
    };
    const keepRows = [
        {
            key: 'birthDate',
            label: t('anonymize.keep.birthDate'),
            value: current(study.patient.birthDate?.slice(0, 10)),
            checked: keepBirthDate,
            set: setKeepBirthDate,
        },
        {
            key: 'sex',
            label: t('anonymize.keep.sex'),
            value: sexSummary || notRecorded,
            checked: keepSex,
            set: setKeepSex,
        },
        {
            key: 'weight',
            label: t('anonymize.keep.weight'),
            value: current(study.patient.weight),
            checked: keepWeight,
            set: setKeepWeight,
        },
        {
            key: 'species',
            label: t('anonymize.keep.species'),
            value: current(study.patient.species),
            checked: keepSpecies,
            set: setKeepSpecies,
        },
        {
            key: 'breed',
            label: t('anonymize.keep.breed'),
            value: current(study.patient.breed),
            checked: keepBreed,
            set: setKeepBreed,
        },
        {
            key: 'seriesDescriptions',
            label: t('anonymize.keep.seriesDescriptions'),
            value: t('anonymize.keep.seriesDescriptionsHint'),
            checked: keepSeriesDescriptions,
            set: setKeepSeriesDescriptions,
        },
    ];
    return (_jsxs("form", { onSubmit: handleSubmit, children: [_jsxs(DialogHeader, { children: [_jsxs(DialogTitle, { className: "flex items-center gap-2", children: [_jsx(EyeOff, { className: "h-5 w-5" }), t('anonymize.title')] }), _jsx(DialogDescription, { children: t('anonymize.intro') })] }), _jsxs("div", { className: "space-y-4 py-4", children: [_jsxs("div", { children: [_jsx(Label, { htmlFor: "anon-patient-name", children: t('anonymize.patientName') }), _jsx(Input, { id: "anon-patient-name", value: patientName, onChange: (e) => setPatientName(e.target.value), className: "mt-2", autoFocus: !isCoarsePointer(), ...NAME_FIELD_PROPS, maxLength: PERSON_NAME_MAX })] }), _jsxs("div", { className: "grid grid-cols-2 gap-3", children: [_jsxs("div", { children: [_jsx(Label, { htmlFor: "anon-patient-id", children: t('anonymize.patientId') }), _jsx(Input, { id: "anon-patient-id", value: patientId, onChange: (e) => setPatientId(e.target.value), className: "mt-2", ...IDENTIFIER_FIELD_PROPS, maxLength: LONG_STRING_MAX })] }), _jsxs("div", { children: [_jsx(Label, { htmlFor: "anon-owner-name", children: t('anonymize.ownerName') }), _jsx(Input, { id: "anon-owner-name", value: ownerName, onChange: (e) => setOwnerName(e.target.value), className: "mt-2", ...NAME_FIELD_PROPS, maxLength: PERSON_NAME_MAX })] })] }), _jsxs("div", { className: "grid grid-cols-2 gap-3", children: [_jsxs("div", { children: [_jsx(Label, { htmlFor: "anon-accession", children: t('anonymize.accession') }), _jsx(Input, { id: "anon-accession", value: accessionNumber, onChange: (e) => setAccessionNumber(e.target.value), className: "mt-2", ...IDENTIFIER_FIELD_PROPS, maxLength: SHORT_STRING_MAX })] }), _jsxs("div", { children: [_jsx(Label, { htmlFor: "anon-study-date", children: t('anonymize.studyDate') }), _jsxs("div", { className: "mt-2 flex gap-2", children: [_jsx(Input, { id: "anon-study-date", type: "date", value: studyDate, max: todayValue, onChange: (e) => setStudyDate(e.target.value) }), _jsx(Button, { type: "button", variant: "outline", size: "sm", onClick: () => setStudyDate(todayValue), children: t('anonymize.today') })] })] })] }), _jsxs("div", { children: [_jsx(Label, { htmlFor: "anon-description", children: t('anonymize.description') }), _jsx(Input, { id: "anon-description", value: description, onChange: (e) => setDescription(e.target.value), className: "mt-2", maxLength: LONG_STRING_MAX })] }), _jsxs("div", { className: "grid grid-cols-2 gap-3", children: [_jsxs("div", { children: [_jsx(Label, { htmlFor: "anon-institution", children: t('anonymize.institution') }), _jsx(Input, { id: "anon-institution", value: institutionName, onChange: (e) => setInstitutionName(e.target.value), className: "mt-2", ...NAME_FIELD_PROPS, maxLength: LONG_STRING_MAX })] }), _jsxs("div", { children: [_jsx(Label, { htmlFor: "anon-referring", children: t('anonymize.referring') }), _jsx(Input, { id: "anon-referring", value: referringPhysician, onChange: (e) => setReferringPhysician(e.target.value), className: "mt-2", ...NAME_FIELD_PROPS, maxLength: PERSON_NAME_MAX })] })] }), _jsxs("fieldset", { className: "space-y-2", children: [_jsx("legend", { className: "text-sm font-medium", children: t('anonymize.keepSection') }), keepRows.map((row) => (_jsxs("div", { className: "flex items-center justify-between gap-3 rounded-md border border-[#2a2f36] px-3 py-2", children: [_jsxs("div", { className: "min-w-0", children: [_jsx(Label, { htmlFor: `anon-keep-${row.key}`, className: "block", children: row.label }), _jsx("span", { className: "text-muted-foreground block truncate text-xs", children: row.value })] }), _jsx(Switch, { id: `anon-keep-${row.key}`, checked: row.checked, onCheckedChange: row.set, "aria-label": row.label })] }, row.key)))] }), _jsx("p", { className: "text-muted-foreground text-xs", children: t('anonymize.burnedInNote') }), problem && (_jsx("p", { className: "text-destructive text-xs", "data-testid": "anonymize-problem", children: t(problem) }))] }), _jsxs(DialogFooter, { children: [_jsx(Button, { type: "button", variant: "outline", onClick: () => onOpenChange(false), disabled: isLoading, children: t('button.cancel') }), _jsx(Button, { type: "submit", disabled: !!problem || isLoading, "data-testid": "anonymize-submit", children: t('anonymize.submit') })] })] }));
}
